import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Menu, X, LogOut, User, LayoutDashboard, Calendar, MessageSquare, Settings, Briefcase, CheckSquare, Send } from 'lucide-react';
import Button from './ui/Button';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const isActive = (path) => location?.pathname === path;

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity" onClick={closeMobileMenu}>
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-xl">B</span>
            </div>
            <span className="text-xl font-bold text-gray-900 hidden sm:inline">BlueMegaphone</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-2">
            <Link
              to="/dashboard"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                isActive('/dashboard') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={closeMobileMenu}
            >
              <LayoutDashboard className="w-5 h-5" />
              <span>Dashboard</span>
            </Link>
            
            <Link
              to="/brand-management"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                isActive('/brand-management') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={closeMobileMenu}
            >
              <Briefcase className="w-5 h-5" />
              <span>Brands</span>
            </Link>

            <Link
              to="/content-scheduler"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                isActive('/content-scheduler') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={closeMobileMenu}
            >
              <Calendar className="w-5 h-5" />
              <span>Content</span>
            </Link>

            <Link
              to="/comment-automation"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                isActive('/comment-automation') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={closeMobileMenu}
            >
              <MessageSquare className="w-5 h-5" />
              <span>Comments</span>
            </Link>

            <Link
              to="/ai-response-review-center"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                isActive('/ai-response-review-center') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={closeMobileMenu}
            >
              <CheckSquare className="w-5 h-5" />
              <span>Review</span>
            </Link>

            <Link
              to="/multi-platform-response-publisher"
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors font-medium ${
                isActive('/multi-platform-response-publisher') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
              }`}
              onClick={closeMobileMenu}
            >
              <Send className="w-5 h-5" />
              <span>Publish</span>
            </Link>
          </nav>

          {/* User Actions */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link 
                  to="/account-settings" 
                  className="hidden md:flex items-center gap-2 px-3 py-2 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  onClick={closeMobileMenu}
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm font-medium">{user?.email?.split('@')?.[0]}</span>
                </Link>
                <Button
                  onClick={handleSignOut}
                  className="bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 shadow-md"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline ml-2">Sign Out</span>
                </Button>
              </>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md"
              >
                Sign In
              </Button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <nav className="flex flex-col gap-1">
              <Link
                to="/dashboard"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-medium ${
                  isActive('/dashboard') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeMobileMenu}
              >
                <LayoutDashboard className="w-5 h-5" />
                <span>Dashboard</span>
              </Link>
              
              <Link
                to="/brand-management"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-medium ${
                  isActive('/brand-management') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeMobileMenu}
              >
                <Briefcase className="w-5 h-5" />
                <span>Brands</span>
              </Link>

              <Link
                to="/content-scheduler"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-medium ${
                  isActive('/content-scheduler') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeMobileMenu}
              >
                <Calendar className="w-5 h-5" />
                <span>Content</span>
              </Link>

              <Link
                to="/comment-automation"
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors font-medium ${
                  isActive('/comment-automation') ? 'bg-blue-100 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
                onClick={closeMobileMenu}
              >
                <MessageSquare className="w-5 h-5" />
                <span>Comments</span>
              </Link>

              {user && (
                <>
                  <div className="border-t border-gray-200 my-2"></div>
                  <Link 
                    to="/account-settings" 
                    className="px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium flex items-center gap-3"
                    onClick={closeMobileMenu}
                  >
                    <Settings className="w-5 h-5" />
                    Account Settings
                  </Link>
                  <button
                    onClick={() => { handleSignOut(); closeMobileMenu(); }}
                    className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium flex items-center gap-3 w-full text-left"
                  >
                    <LogOut className="w-5 h-5" />
                    Sign Out
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}